/**
 * useReportMeta — R3/Stream 1 (Monthly-Report-Paritaet)
 *
 * Metadaten-Hooks fuer den Monatsreport. Alle drei Endpoints wurden in der
 * BE-Phase dieses Streams neu angelegt und ersetzen FE-Hartkodierungen:
 *
 *   GET /api/v2/reports/available_months  -> Monatsliste (v1 "Select a month")
 *   GET /api/v2/reports/time_zones        -> vollstaendige Zeitzonenliste
 *   GET /api/v2/reports/email_recipients  -> Vorbefuellung des Versand-Dialogs
 *
 * Die Endpoints sind noch nicht in @heliumtracker/api-types generiert
 * (BE-11 offen), deshalb ein schmales, lokal typisiertes Client-Interface
 * statt `as any` — gleiches Muster wie useMonthlyReport.ts.
 */
import { useQuery } from '@tanstack/react-query'
import apiClient from '../../lib/api-client'

// --- Typen ---

export type AvailableMonth = {
  /** "YYYY-MM" */
  value: string
  /** z.B. "August 2026" */
  label: string
}

export type AvailableMonths = {
  months: AvailableMonth[]
  /** 24 fuer Premium, 0 fuer Free — Free = nur laufender Monat (v1-Regel). */
  maxMonths: number
  premium: boolean
  /** Default-Monat ("YYYY-MM") */
  defaultMonth: string
}

export type ReportTimeZone = {
  /** Rails-Zonenname — DAS ist der Wert, der an das BE geschickt wird. */
  name: string
  /** IANA-Identifier (z.B. "Europe/Berlin") fuer Browser-Matching. */
  identifier: string
  /** z.B. "+01:00" */
  offset: string
  /** z.B. "(GMT+01:00) Berlin" */
  label: string
}

export type ReportTimeZones = {
  timeZones: ReportTimeZone[]
  /** Default-Zone (Rails-Name) aus dem User-Profil. */
  defaultZone: string
}

export type EmailRecipientContact = {
  id: number
  name: string
  email: string
  monthlyReport: boolean
}

export type EmailRecipients = {
  accountEmail: string
  accountEnabled: boolean
  contacts: EmailRecipientContact[]
}

// --- Lese-Helfer (kein `as any`) ---

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null
}

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback
}

function num(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback
}

function bool(v: unknown): boolean {
  return v === true
}

type RawGetClient = {
  GET: (url: string) => Promise<{ data?: unknown; error?: unknown }>
}

function unwrap(raw: unknown): Record<string, unknown> {
  const root = isRecord(raw) ? raw : {}
  return isRecord(root.data) ? root.data : {}
}

async function get(url: string): Promise<Record<string, unknown>> {
  const client = apiClient as unknown as RawGetClient
  const result = await client.GET(url)
  if (result.error) throw result.error
  return unwrap(result.data)
}

// --- Mapper (exportiert fuer Tests) ---

export function mapAvailableMonths(raw: unknown): AvailableMonths {
  const data = unwrap(raw)
  const months = Array.isArray(data.months)
    ? data.months.filter(isRecord).map((m) => ({ value: str(m.value), label: str(m.label) }))
    : []
  return {
    months: months.filter((m) => m.value !== ''),
    maxMonths: num(data.max_months),
    premium: bool(data.premium),
    defaultMonth: str(data.default),
  }
}

export function mapTimeZones(raw: unknown): ReportTimeZones {
  const data = unwrap(raw)
  const zones = Array.isArray(data.time_zones)
    ? data.time_zones.filter(isRecord).map((z) => {
        const name = str(z.name)
        return {
          name,
          identifier: str(z.identifier, name),
          offset: str(z.offset),
          label: str(z.label, name),
        }
      })
    : []
  return {
    timeZones: zones.filter((z) => z.name !== ''),
    defaultZone: str(data.default),
  }
}

export function mapEmailRecipients(raw: unknown): EmailRecipients {
  const data = unwrap(raw)
  const contacts = Array.isArray(data.contacts)
    ? data.contacts.filter(isRecord).map((c) => ({
        id: num(c.id),
        name: str(c.name),
        email: str(c.email),
        monthlyReport: bool(c.monthly_report),
      }))
    : []
  return {
    accountEmail: str(data.account_email),
    accountEnabled: bool(data.account_enabled),
    contacts,
  }
}

// --- Hooks ---

export function useAvailableMonths() {
  const query = useQuery<AvailableMonths>({
    queryKey: ['reports', 'available_months'],
    queryFn: async () => mapAvailableMonths({ data: await get('/api/v2/reports/available_months') }),
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  return {
    months: query.data?.months ?? [],
    maxMonths: query.data?.maxMonths,
    premium: query.data?.premium ?? false,
    defaultMonth: query.data?.defaultMonth,
    isLoading: query.isLoading,
    isError: query.isError,
  }
}

export function useTimeZones() {
  const query = useQuery<ReportTimeZones>({
    queryKey: ['reports', 'time_zones'],
    queryFn: async () => mapTimeZones({ data: await get('/api/v2/reports/time_zones') }),
    staleTime: 60 * 60 * 1000,
    retry: false,
  })

  return {
    timeZones: query.data?.timeZones ?? [],
    defaultZone: query.data?.defaultZone,
    isLoading: query.isLoading,
    isError: query.isError,
  }
}

/** `enabled` erlaubt es, die Empfaengerliste erst beim Oeffnen des Dialogs zu laden. */
export function useEmailRecipients(enabled = true) {
  const query = useQuery<EmailRecipients>({
    queryKey: ['reports', 'email_recipients'],
    queryFn: async () => mapEmailRecipients({ data: await get('/api/v2/reports/email_recipients') }),
    enabled,
    retry: false,
  })

  return {
    recipients: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
  }
}
